import {Box, Divider, Stack, Typography} from '@mui/material';
import React, {useEffect, useState, useMemo, useRef} from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {useTranslation, Trans} from 'react-i18next';
import './TextComponent.scss';
import ArrRadio from '../../../../common/arr-radio/ArrRadio';
import {TextFormatEnum} from '../../../../../schemas/productions/productionSchema';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {useGetTextComponentReproduction} from '../../../../../hooks/productions/useReProductions';
import {ModuleType, FormMode} from '../../ReProductionUtils';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {
  GetTextComponentResponse,
  AnsiFormat,
} from '../../../../../api/types/production.type';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrModal from '../../../../common/arr-modal/ArrModal';

interface TextComponentProps {
  ansiList: AnsiFormat[];
  productionId: number;
  isProductionSet: boolean;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
  isReProduction?: boolean;
}

const DEFAULT_TEXT_COMPONENT_ANSI_TYPE_ID = '21027';

const ENCODING_MAP = Object.fromEntries(
  TextFormatEnum.options.map((value, index) => [index, value])
);

const ansiIdsEqual = (
  a: string | number | null | undefined,
  b: string | number | null | undefined
) => String(a ?? '') === String(b ?? '');

const findAnsiDropdownItem = (
  items: {id: string | number; name: string}[],
  formValue: string | number | null | undefined
) =>
  formValue == null || formValue === ''
    ? null
    : (items.find((opt) => ansiIdsEqual(opt.id, formValue)) ?? null);

const TextComponent: React.FC<TextComponentProps> = ({
  ansiList,
  productionId,
  //isProductionSet,
  mode = FormMode.CREATE,
  isTemplate = false,
  //  templateId,
  isReProduction = false,
}) => {
  const {t} = useTranslation();

  const {control, setValue, reset, getValues} = useFormContext();

  const hasHydratedTextRef = useRef(false);

  const textFormat = useWatch({control, name: 'textComponentData.textFormat'});
  const [ANSI, UnicodeUTF16] = TextFormatEnum.options;

  const [open, setOpen] = useState(false);
  const isViewMode = mode === FormMode.VIEW;
  const isReProductionViewMode = isViewMode && isReProduction; // ← scoped to re-production only

  // Get Redux state
  const {securityGroupId} = useSelector((state: RootState) => state.production);

  // Determine moduleType from isProductionSet using enum
  // const moduleType = useMemo(
  //   () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
  //   [isProductionSet]
  // );

  // API call to get text component data
  const {
    data: textComponentDataFromProduction,
    isLoading: isTextComponentLoadingFromProduction,
    error: textComponentErrorFromProduction,
  } = useGetTextComponentReproduction(
    {
      SecurityGroupId: securityGroupId,
      moduletype: ModuleType.REPRODUCTION,
      productionId: productionId,
      exportProductionId: 0,
    },
    !!securityGroupId && productionId > 0 && !isTemplate
  );

  // Use the appropriate data based on isTemplate flag - memoize to prevent infinite loops
  const textComponentData: GetTextComponentResponse | undefined =
    useMemo(() => {
      return textComponentDataFromProduction;
    }, [textComponentDataFromProduction]);

  const isTextComponentLoading = isTextComponentLoadingFromProduction;
  const textComponentError = textComponentErrorFromProduction;

  // Convert ANSI list to dropdown format
  const ansiDropdownItems = ansiList.map((item) => ({
    id: item.ansiTypeID,
    name: item.ansiTypeLabel,
  }));

  // Update form state based on API response
  useEffect(() => {
    if (textComponentData?.textComponentModelDetails) {
      const modelDetails = textComponentData.textComponentModelDetails;
      const textFormat = ENCODING_MAP[modelDetails.eTextFormat];
      const selectedANSIType =
        modelDetails.selectedANSIType != null &&
        String(modelDetails.selectedANSIType).trim() !== ''
          ? String(modelDetails.selectedANSIType)
          : ansiDropdownItems[0]?.id != null
            ? String(ansiDropdownItems[0].id)
            : DEFAULT_TEXT_COMPONENT_ANSI_TYPE_ID;
      const shouldSkipOCR = modelDetails.shouldSkipOCR;
      const shouldExportLargeTextFiles =
        modelDetails.shouldExportLargeTextFiles;
      const produceLoadFile = modelDetails.produceLoadFile;

      const currentValues = getValues();
      reset(
        {
          ...currentValues,
          textComponentData: {
            textFormat,
            eTextFormat: modelDetails.eTextFormat,
            isAnsi: modelDetails.eTextFormat === 0,
            selectedANSIType,
            shouldSkipOCR,
            shouldExportLargeTextFiles,
            produceLoadFile,
          },
        },
        {keepDirty: false}
      );
      hasHydratedTextRef.current = true;
    }
  }, [textComponentData, reset, getValues]);

  useEffect(() => {
    if (textFormat === ANSI) {
      setValue('textComponentData.eTextFormat', 0, {
        shouldValidate: true,
        shouldDirty: false,
      });
      setValue('textComponentData.isAnsi', true, {
        shouldValidate: true,
        shouldDirty: false,
      });
    } else if (textFormat === UnicodeUTF16) {
      setValue('textComponentData.eTextFormat', 1, {
        shouldValidate: true,
        shouldDirty: false,
      });
      setValue('textComponentData.isAnsi', false, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [textFormat, ANSI, UnicodeUTF16, setValue]);

  // Show loading state
  if (isTextComponentLoading) {
    return (
      <Box className="arr-text-encoding" sx={{textAlign: 'left'}}>
        <Typography variant="body1">Loading text component data...</Typography>
      </Box>
    );
  }

  // Show error state
  if (textComponentError) {
    return (
      <Box className="arr-text-encoding" sx={{textAlign: 'left'}}>
        <Typography variant="body1" color="error">
          Error loading text component data: {textComponentError.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className={`arr-text-encoding${isReProductionViewMode ? ' text-component--view-mode' : ''}`}
      sx={{textAlign: 'left'}}
    >
      <Typography
        component="h3"
        variant="body1"
        className={`typo-text subhead4_size text-headings`}
      >
        {t(
          'productionsAndExports.createNewProduction.productionComponents.textComponent.encodingLabel'
        )}
      </Typography>
      <Box
        className="arr-text-encoding-format__wrapper"
        display="flex"
        alignItems="center"
      >
        <Stack
          direction="row"
          flexWrap={'wrap'}
          spacing={8}
          alignItems="center"
        >
          <Controller
            name="textComponentData.textFormat"
            control={control}
            render={({field}) => (
              <ArrRadio
                dataTestId={t(
                  'productionsAndExports.createNewProduction.productionComponents.textComponent.encodingOptions.ansi'
                )}
                size="small"
                {...field}
                data={[
                  {
                    label: t(
                      'productionsAndExports.createNewProduction.productionComponents.textComponent.encodingOptions.ansi'
                    ),
                    value: ANSI,
                  },
                ]}
                className="text-headings"
                disabled={isViewMode}
              />
            )}
          ></Controller>
          <Controller
            name="textComponentData.selectedANSIType"
            control={control}
            render={({field}) => (
              <ArrSingleSelectDropdown
                dropdownLabel="Select ANSI"
                dropdownItems={ansiDropdownItems}
                selectedValues={findAnsiDropdownItem(
                  ansiDropdownItems,
                  field.value
                )}
                onSelectionChange={(item) =>
                  field.onChange(
                    item?.id != null && item.id !== '' ? String(item.id) : ''
                  )
                }
                onBlur={field.onBlur}
                sizeVariant="medium"
                customWidth={15}
                disabled={isViewMode || textFormat === UnicodeUTF16}
                searchable
                showNoResultsText
                dataTestId="asd-select-ansi"
              />
            )}
          ></Controller>
          <Controller
            name="textComponentData.textFormat"
            control={control}
            render={({field}) => (
              <ArrRadio
                dataTestId={t(
                  'productionsAndExports.createNewProduction.productionComponents.textComponent.encodingOptions.unicode'
                )}
                size="small"
                {...field}
                data={[
                  {
                    label: t(
                      'productionsAndExports.createNewProduction.productionComponents.textComponent.encodingOptions.unicode'
                    ),
                    value: UnicodeUTF16,
                  },
                ]}
                className="text-headings"
                disabled={isViewMode}
              />
            )}
          ></Controller>
        </Stack>
      </Box>
      <Box
        className="arr-text-encoding-info-points"
        component="ul"
        aria-labelledby="info-points-heading"
      >
        {Object.entries(
          t(
            'productionsAndExports.createNewProduction.productionComponents.textComponent.infoPoints'
          )
        ).map(([key, point]) => (
          <li className="typo-text body3_size text-body-subtle" key={key}>
            {point}
          </li>
        ))}
      </Box>
      <Divider></Divider>
      <Typography
        component="h3"
        variant="body1"
        className="arr-text-ocr__wrapper typo-text subhead4_size text-headings"
        display="flex"
        gap={1}
      >
        {t(
          'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrSection.label'
        )}
        <ArrTooltipWrapper
          title={t(
            'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrSection.tooltip.title'
          )}
          trigger={<InfoOutlinedIcon className="info-icon" />}
        >
          <Trans
            i18nKey="productionsAndExports.createNewProduction.productionComponents.textComponent.ocrSection.tooltip.content"
            components={{
              br: <br />,
              strong: <strong />,
              p: <p />,
            }}
          />
        </ArrTooltipWrapper>
      </Typography>

      <Stack direction="row" flexWrap={'wrap'} spacing={8} marginTop={3}>
        <Controller
          name="textComponentData.shouldSkipOCR"
          control={control}
          render={({field}) => (
            <ArrRadio
              dataTestId={t(
                'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.noOcr'
              )}
              size="small"
              name="shouldSkipOCRTrue"
              value={String(field.value)}
              onChange={(value) => {
                if (isViewMode) return;
                field.onChange(value === 'true');
              }}
              data={[
                {
                  label: t(
                    'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.noOcr'
                  ),
                  value: 'true',
                },
              ]}
              className="text-headings"
              disabled={isViewMode}
            />
          )}
        ></Controller>
        <Controller
          name="textComponentData.shouldSkipOCR"
          control={control}
          render={({field}) => (
            <ArrRadio
              dataTestId={t(
                'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocr'
              )}
              size="small"
              name="shouldSkipOCRFalse"
              value={String(field.value)}
              onChange={(value) => {
                if (isViewMode) return;
                if (value === 'false') {
                  setOpen(true);
                  field.onChange(value === 'false');
                } else {
                  field.onChange(value === 'true');
                }
              }}
              data={[
                {
                  label: t(
                    'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocr'
                  ),
                  value: 'false',
                },
              ]}
              className="text-headings"
              disabled={isViewMode}
            />
          )}
        ></Controller>
      </Stack>
      <Controller
        name="textComponentData.shouldExportLargeTextFiles"
        control={control}
        render={({field}) => (
          <Box className="arr-text-large-txt-files native-toggle">
            <ArrToggle
              {...field}
              disabled={isViewMode}
              checked={field.value}
              dataTestId="text-component-toggle"
            />

            <Typography
              component="h3"
              className="toggle-text typo-text subhead5_size"
              display="flex"
              alignItems="center"
              gap={1}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.textComponent.exportLargeTextFiles.label'
              )}
              <ArrTooltipWrapper
                title={t(
                  'productionsAndExports.createNewProduction.productionComponents.textComponent.exportLargeTextFiles.tooltip.title'
                )}
                trigger={<InfoOutlinedIcon className="info-icon-sm" />}
              >
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.productionComponents.textComponent.exportLargeTextFiles.tooltip.content"
                  components={{
                    br: <br />,
                    strong: <strong />,
                    p: <p />,
                  }}
                />
              </ArrTooltipWrapper>
            </Typography>
          </Box>
        )}
      ></Controller>

      <Controller
        name="textComponentData.produceLoadFile"
        control={control}
        render={({field}) => (
          <Box className="arr-text-produce-load-file native-toggle">
            <ArrToggle
              {...field}
              disabled={isViewMode}
              checked={field.value}
              dataTestId="load-file-toggle"
            />

            <Typography className="toggle-text typo-text subhead5_size">
              {t(
                'productionsAndExports.createNewProduction.productionComponents.textComponent.generateTextLoadFile'
              )}
            </Typography>
          </Box>
        )}
      ></Controller>
      <ArrModal
        showModal={open}
        onClose={() => setOpen(false)}
        headerTitle={t(
          'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocrConfirmation.title'
        )}
        headerTitleClassName="headline3_size text-headings"
        className="text-component-modal"
        dataTestId="text-component-modal"
        buttons={[
          {
            id: '1',
            label: t(
              'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocrConfirmation.confirm'
            ),
            onClick: () => {
              setValue('textComponentData.shouldSkipOCR', false, {
                shouldValidate: true,
              });
              setOpen(false);
            },
            variant: 'primary',
            dataTestId: 'tcm-confirm-btn',
          },
          {
            id: '2',
            label: t(
              'productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocrConfirmation.cancel'
            ),
            onClick: () => setOpen(false),
            variant: 'secondary',
            dataTestId: 'tcm-cancel-btn',
          },
        ]}
      >
        <Typography
          component="div"
          className="typo-text body2_size desc-text-color"
          color="white"
          data-testid={`assignment-instructions`}
        >
          <Trans
            i18nKey="productionsAndExports.createNewProduction.productionComponents.textComponent.ocrOptions.ocrConfirmation.description"
            components={{
              br: <br />,
              strong: <strong />,
              p: <p />,
            }}
          />
        </Typography>
      </ArrModal>
    </Box>
  );
};

export default TextComponent;
